import { PageSize } from '@/components/DataTable/PageSizeSelector'
import { getPageSize, setPageSize } from '@/stores/datatable'
import { PAGE_SIZE } from '@repo/constants'
import { useCallback } from 'react'

type UseTablePaginationProps = {
  navigate: (options: { search: Record<string, unknown> }) => void
  search: Record<string, unknown>
  defaultPageSize?: PageSize
  pageParamName?: string
}

/**
 * Hook for managing table pagination state with URL search parameters.
 *
 * This hook provides a consistent way to handle page size changes across different
 * table implementations that use manual pagination (server-side) with URL state management.
 *
 * @param navigate - Navigation function from TanStack Router
 * @param search - Current search parameters object
 * @param defaultPageSize - Default page size for the table
 * @param pageParamName - URL parameter name for page size (defaults to 'pageSize')
 *
 * @returns Object containing:
 *   - pageSize: Current page size derived from URL params or default
 *   - onPageSizeChange: Handler function for page size changes
 *
 * @example
 * ```tsx
 * // In a DataTable component with TanStack Router
 * const navigate = route.useNavigate()
 * const search = route.useSearch()
 *
 * const { pageSize, onPageSizeChange } = useTablePagination({
 *   navigate,
 *   search,
 *   defaultPageSize: 30,
 * })
 *
 * return (
 *   <DataTable
 *     pageSize={pageSize}
 *     onPageSizeChange={onPageSizeChange}
 *     // ... other props
 *   />
 * )
 * ```
 */
export function useTablePagination({
  navigate,
  search,
  defaultPageSize = PAGE_SIZE,
  pageParamName = 'pageSize',
}: UseTablePaginationProps) {
  // Derive current page size from search params or use default value
  const pageSize = (search[pageParamName] as PageSize) ?? getPageSize() ?? defaultPageSize

  // Page size change handler
  const onPageSizeChange = useCallback(
    (newPageSize: PageSize) => {
      setPageSize(newPageSize)
      navigate({
        search: {
          ...search,
          [pageParamName]: newPageSize !== defaultPageSize ? newPageSize : undefined, // Only include if different from default
          page: 1, // Reset to first page when changing page size
        },
      })
    },
    [navigate, search, defaultPageSize, pageParamName],
  )

  return {
    pageSize,
    onPageSizeChange,
  }
}
